import React, { useState, useEffect } from 'react';
import { X, Activity, Users, TrendingUp, CreditCard } from 'lucide-react';
import organizationService from '../../../services/organizationService';

export default function OrganizationHealthModal({ isOpen, onClose, organization }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && organization) {
      loadHealthMetrics();
    }
  }, [isOpen, organization]);

  const loadHealthMetrics = async () => {
    try {
      setLoading(true);
      const metrics = await organizationService?.getOrganizationHealth(organization?.id);
      setHealth(metrics);
    } catch (error) {
      console.error('Error loading health metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Organization Health</h2>
              <p className="text-sm text-gray-600">{organization?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : health ? (
            <div className="space-y-6">
              {/* Health Score */}
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${getHealthColor(health?.healthScore)}`}>
                  <div>
                    <div className="text-4xl font-bold">{health?.healthScore}</div>
                    <div className="text-sm font-medium">Health Score</div>
                  </div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">Total Members</p>
                      <p className="text-2xl font-bold text-blue-600">{health?.totalMembers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-sm text-gray-600">Active Members</p>
                      <p className="text-2xl font-bold text-green-600">{health?.activeMembers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Activity className="w-8 h-8 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Activity Rate</p>
                      <p className="text-2xl font-bold text-purple-600">{health?.activityRate}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-orange-600" />
                    <div>
                      <p className="text-sm text-gray-600">Subscription</p>
                      <p className="text-lg font-bold text-orange-600">{health?.subscriptionTier}</p>
                      <p className="text-xs text-gray-500 capitalize">{health?.subscriptionStatus}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Health Insights */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Health Insights</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  {health?.activityRate >= 70 && (
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      High member engagement - {health?.activityRate}% active in last 30 days
                    </li>
                  )}
                  {health?.activityRate < 50 && (
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Low member engagement - consider re-engagement campaigns
                    </li>
                  )}
                  {health?.subscriptionStatus === 'active' && (
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Active subscription with {health?.subscriptionTier} plan
                    </li>
                  )}
                  {health?.subscriptionStatus === 'trial' && (
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                      Trial period - follow up for conversion
                    </li>
                  )}
                  {health?.totalMembers < 5 && (
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      Small team - opportunity for growth
                    </li>
                  )}
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Unable to load health metrics</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}